import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Heart } from "lucide-react";
import { ShopifyProduct, formatPrice } from "@/lib/shopify";
import { formatProductTitle } from "@/lib/formatProductTitle";
import { useCartStore } from "@/stores/cartStore";
import { useWishlistStore } from "@/stores/wishlistStore";
import { openCartDrawer } from "@/components/CartDrawer";
import { shopifyImageUrl } from "@/lib/imageUtils";
import { toast } from "sonner";
import { COLOR_HEX, LIGHT_COLORS } from "@/lib/colorUtils";

interface ShopifyProductCardProps {
  product: ShopifyProduct;
  index?: number;
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.03 },
  }),
};

/**
 * Extract unique color swatches from Shopify variant option data.
 * Looks for "Color" or "Colour" option, falls back to title parsing.
 */
function extractColorSwatches(product: ShopifyProduct): string[] {
  const hexes: string[] = [];
  const seen = new Set<string>();
  const sorted = Object.keys(COLOR_HEX).sort((a, b) => b.length - a.length);

  const addColor = (hex: string) => {
    if (!seen.has(hex)) {
      seen.add(hex);
      hexes.push(hex);
    }
  };

  // 1. Primary: extract from variant selectedOptions where name is Color/Colour
  for (const edge of product.node.variants.edges) {
    for (const opt of edge.node.selectedOptions || []) {
      if (opt.name.toLowerCase() !== "color" && opt.name.toLowerCase() !== "colour") continue;
      const val = opt.value.toLowerCase();
      // Check each color value within the option (may contain "/")
      const parts = val.split("/").map((p) => p.trim());
      for (const part of parts) {
        for (const color of sorted) {
          if (part.includes(color)) {
            addColor(COLOR_HEX[color]);
            break;
          }
        }
      }
    }
  }

  // 2. Fallback: extract from product.options
  if (hexes.length === 0) {
    const colorOption = product.node.options?.find(
      (o) => o.name.toLowerCase() === "color" || o.name.toLowerCase() === "colour"
    );
    if (colorOption) {
      for (const val of colorOption.values) {
        const lower = val.toLowerCase();
        const parts = lower.split("/").map((p) => p.trim());
        for (const part of parts) {
          for (const color of sorted) {
            if (part.includes(color)) {
              addColor(COLOR_HEX[color]);
              break;
            }
          }
        }
      }
    }
  }

  // 3. Last resort: parse product title
  if (hexes.length === 0) {
    const title = product.node.title.toLowerCase();
    for (const color of sorted) {
      if (title.includes(color)) {
        addColor(COLOR_HEX[color]);
      }
    }
  }

  return hexes;
}

const GRID_IMG_WIDTH = 400;
const HOVER_IMG_WIDTH = 600;

const ShopifyProductCard = ({ product, index = 0 }: ShopifyProductCardProps) => {
  const { node } = product;
  const displayTitle = formatProductTitle(node.title);
  const price = node.priceRange.minVariantPrice;
  const compareAt = node.compareAtPriceRange?.minVariantPrice;
  const hasDiscount = compareAt && parseFloat(compareAt.amount) > parseFloat(price.amount);
  const hasSaleTag = node.tags?.some((t) => t.toLowerCase() === "sale");
  const isOnSale = hasDiscount || hasSaleTag;
  const mainImage = node.images.edges[0]?.node;
  const hoverImage = node.images.edges[1]?.node;
  const addItem = useCartStore((s) => s.addItem);
  const toggleWishlist = useWishlistStore((s) => s.toggleItem);
  const isInWishlist = useWishlistStore((s) => s.isInWishlist(node.id));

  const altText = mainImage?.altText || `${displayTitle} — hand-crocheted NOTT bag`;
  const hoverAltText = hoverImage?.altText || `${displayTitle} — alternate view`;

  const firstVariant = node.variants.edges[0]?.node;
  const canQuickAdd = firstVariant?.availableForSale !== false;
  const colorSwatches = extractColorSwatches(product);
  const isNew = node.tags?.includes("Spring 2026") || node.tags?.includes("New");

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!firstVariant) return;
    try {
      await addItem({
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || [],
      });
      toast.success("Added to cart", { description: displayTitle });
      openCartDrawer();
    } catch {
      toast.error("Could not add to cart");
    }
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
    toast(isInWishlist ? "Removed from wishlist" : "Saved to wishlist", {
      description: displayTitle,
    });
  };

  return (
    <motion.div
      custom={index}
      variants={item}
      initial="hidden"
      animate="show"
    >
      <Link to={`/product/${node.handle}`} className="group block">
        <div className="relative aspect-[3/4] overflow-hidden rounded-sm bg-muted mb-3">
          {mainImage && (
            <img
              src={shopifyImageUrl(mainImage.url, GRID_IMG_WIDTH)}
              alt={altText}
              width={GRID_IMG_WIDTH}
              height={Math.round(GRID_IMG_WIDTH * (4 / 3))}
              decoding="async"
              loading="lazy"
              className={`w-full h-full object-cover transition-all duration-700 ${hoverImage ? "group-hover:opacity-0" : "group-hover:scale-105"}`}
            />
          )}
          {hoverImage && (
            <img
              src={shopifyImageUrl(hoverImage.url, HOVER_IMG_WIDTH)}
              alt={hoverAltText}
              decoding="async"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            />
          )}

          {/* Badges */}
          {isOnSale && (
            <span className="absolute top-3 left-3 bg-destructive text-destructive-foreground font-body text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-sm">
              Sale
            </span>
          )}
          {!isOnSale && node.tags?.includes("Best Seller") && (
            <span className="absolute top-3 left-3 bg-foreground text-primary-foreground font-body text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-sm">
              Best Seller
            </span>
          )}
          {!isOnSale && !node.tags?.includes("Best Seller") && isNew && (
            <span className="absolute top-3 left-3 bg-accent text-accent-foreground font-body text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-sm">
              New In
            </span>
          )}

          {/* Wishlist heart */}
          <button
            onClick={handleToggleWishlist}
            className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
              isInWishlist
                ? "bg-foreground text-primary-foreground"
                : "bg-background/80 backdrop-blur-sm text-muted-foreground opacity-0 group-hover:opacity-100"
            }`}
            aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
          >
            <Heart size={14} className={isInWishlist ? "fill-current" : ""} />
          </button>

          {/* Quick-add button */}
          {canQuickAdd && (
            <button
              onClick={handleQuickAdd}
              className="absolute bottom-3 right-3 w-9 h-9 bg-background/90 backdrop-blur-sm border border-border rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-foreground hover:text-background hover:border-foreground"
              aria-label={`Quick add ${displayTitle} to cart`}
            >
              <ShoppingBag size={14} />
            </button>
          )}
        </div>

        <h3 className="font-body text-sm text-foreground group-hover:text-muted-foreground transition-colors">
          {displayTitle}
        </h3>

        {/* Color swatches — dynamic from Shopify variant data */}
        {colorSwatches.length > 0 && (
          <div className="flex items-center gap-[2px] mt-1.5" aria-label="Available colors">
            <span
              className="w-[10px] h-[10px] rounded-full"
              style={{
                backgroundColor: colorSwatches[0],
                border: LIGHT_COLORS.has(colorSwatches[0])
                  ? "1px solid #E5E5E5"
                  : "1px solid transparent",
              }}
              aria-label={
                Object.entries(COLOR_HEX).find(([, v]) => v === colorSwatches[0])?.[0] || "color"
              }
            />
          </div>
        )}

        <div className="flex items-center gap-2 mt-0.5">
          <p className="font-body text-sm text-muted-foreground">
            {formatPrice(price.amount, price.currencyCode)}
          </p>
          {hasDiscount && (
            <p className="font-body text-xs text-muted-foreground/50 line-through">
              {formatPrice(compareAt.amount, compareAt.currencyCode)}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default ShopifyProductCard;
